import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useHistory } from 'react-router';
import { Waypoint } from 'react-waypoint';
import { v4 as uuid } from 'uuid';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useSelectionState } from '../../../../hooks/useSelectionState';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { numberToUSDCurrencyStringValue } from '../../../../models/UiUtils';
import {
	invalidateInsurancePolicyFilter,
	invalidateInsurancePolicySummary,
	useExportInsurancePoliciesMutation,
	useInsurancePolicyContactsMutation,
	useInsurancePolicyFilter,
	useInsurancePolicySummaryQuery,
} from '../../../../queries';
import { BreadcrumbBar } from '../../../components-v2/BreadcrumbBar';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { Column, Page } from '../../../components-v2/PageLayout';
import { SimpleAutoCompleteSearchField } from '../../../components/autocomplete/SimpleAutoCompleteSearchField';
import { Checkbox } from '../../../components/Checkbox';
import { CloseButton } from '../../../components/CloseButton';
import {
	Databoard,
	DataboardActions,
	DataboardBody,
	DataboardFilters,
	DataboardNoResults,
	DataboardStat,
	DataboardStatLabel,
	DataboardStats,
	DataboardStatTitle,
	DataboardStatValue,
} from '../../../components/Databoard';
import { Badge } from '../../../components/dataBoards/Badge';
import { DeleteModal, DeleteType } from '../../../components/dataBoards/DeleteModal';
import { EditPolicyModal } from '../../../components/dataBoards/policies/EditPolicyModal';
import { PolicyPropertyOption } from '../../../components/dataBoards/policies/models';
import { MoneyFilterDropdown } from '../../../components/dataBoards/policies/MoneyFilterDropdown';
import { PolicyCarrierSearch } from '../../../components/dataBoards/policies/PolicyCarrierSearch';
import { PolicyHolderDetailsFlyout } from '../../../components/dataBoards/policies/PolicyHolderDetailsFlyout';
import { PolicyLineOfBusinessFilterDropdown } from '../../../components/dataBoards/policies/PolicyLineOfBusinessFilterDropdown';
import { RenewalDateFilterDropdown } from '../../../components/dataBoards/policies/RenewalDateFilterDropdown';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../components/DataGrid';
import { ISelectOption } from '../../../components/DeprecatedSelect';
import { ExportConfirmationModalV2 } from '../../../components/ExportConfirmation';
import { FabContext } from '../../../components/FabContext';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/Select';
import { DropDownIcon } from '../../../components/svgs/icons/DropDownIcon';
import { LightningBulbIcon } from '../../../components/svgs/icons/LightBulbIcon';
import { SearchIcon } from '../../../components/svgs/icons/SearchIcon';
import { TextInput } from '../../../components/TextInput';
import { TinyPopover } from '../../../components/TinyPopover';
import { UsersActionBar } from '../../../components/UsersActionBar';
import { ROUTES } from '../../../constants/routes';
import { white } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet as dbStyleSheet } from '../styles';
import { styleSheet } from './styles';

enum MenuItem {
	Edit = 'Edit',
	Delete = 'Delete',
}
export const PoliciesBoard = () => {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const { logApiError } = useEventLogging('PoliciesBoard');
	const [key, setKey] = React.useState(uuid());
	const history = useHistory();

	const insuranceIntegrationEnabled = userSession.hasInsuranceIntegrationConfigured;
	const canEdit = !insuranceIntegrationEnabled;

	const PolicyPropertyOptions = Object.keys(PolicyPropertyOption)
		.map(option => {
			const selectOption: ISelectOption = {
				id: option,
				text: PolicyPropertyOption[option as keyof typeof PolicyPropertyOption],
				dataContext: option,
			};

			return selectOption;
		})
		.filter(s => (s.id === 'ServicingRole1User' || s.id === 'ServicingRole2User' ? canEdit : true));

	const [selectedSearchProperty, setSelectedSearchProperty] = React.useState(PolicyPropertyOptions[0]);
	const [searchFieldValue, setSearchFieldValue] = React.useState('');
	const debouncedSearchFieldValue = useDebounceValue(searchFieldValue, 500);
	const [selectedCarrierId, setSelectedCarrierId] = React.useState<string>(null);

	const [minimumPremium, setMinimumPremium] = React.useState<string>('');
	const [maximumPremium, setMaximumPremium] = React.useState<string>('');

	const [minimumCommissions, setMinimumCommissions] = React.useState<string>('');
	const [maximumCommissions, setMaximumCommissions] = React.useState<string>('');

	const [selectedMinimumRenewalDate, setSelectedMinimumRenewalDate] = React.useState<string>(null);
	const [selectedMaximumRenewalDate, setSelectedMaximumRenewalDate] = React.useState<string>(null);

	const [policyToEdit, setPolicyToEdit] = React.useState<Api.IInsurancePolicy>(null);
	const [policyToDelete, setPolicyToDelete] = React.useState<Api.IInsurancePolicy>(null);

	const [isAddPopoverOpen, setIsAddPopoverOpen] = React.useState(false);
	const [isEditOpen, setIsEditOpen] = React.useState(false);
	const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
	const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);

	const [contactToView, setContactToView] = React.useState<Api.IContact>(null);

	const [selectedContact, setSelectedContact] = React.useState<Api.IContact>(null);
	const [selectedOwner, setSelectedOwner] = React.useState<Api.IContact>(null);
	const [selectedServicingRole1User, setSelectedServicingRole1User] = React.useState<Api.IContact>(null);
	const [selectedServicingRole2User, setSelectedServicingRole2User] = React.useState<Api.IContact>(null);

	const [selectedPolicyLineOfBusinessIds, setSelectedPolicyLineOfBusinessIds] = React.useState<string[]>([]);

	const [selectedPolicyType, setSelectedPolicyType] = React.useState<Api.InsurancePolicyFilterProperty>(
		Api.InsurancePolicyFilterProperty.RenewalDate
	);

	const hasSearchValues =
		!!debouncedSearchFieldValue ||
		selectedContact ||
		selectedOwner ||
		selectedPolicyLineOfBusinessIds.length > 0 ||
		selectedCarrierId ||
		minimumPremium ||
		maximumPremium ||
		selectedMinimumRenewalDate ||
		selectedMaximumRenewalDate ||
		minimumCommissions ||
		maximumCommissions ||
		selectedServicingRole1User ||
		selectedServicingRole2User;

	const policyFilterCriteria = React.useMemo(() => {
		const criterias: Api.IInsurancePolicyFilterCriteria[] = [];

		if (debouncedSearchFieldValue) {
			criterias.push({
				property: Api.InsurancePolicyFilterProperty.PolicyNumber,
				value: debouncedSearchFieldValue,
			});
		}

		if (selectedContact) {
			criterias.push({
				property: Api.InsurancePolicyFilterProperty.Contact,
				value: selectedContact.id,
			});
		}

		if (selectedOwner) {
			criterias.push({
				property: Api.InsurancePolicyFilterProperty.OwnerId,
				value: selectedOwner.id,
			});
		}

		if (!!minimumPremium || !!maximumPremium) {
			if (minimumPremium) {
				criterias.push({
					property: Api.InsurancePolicyFilterProperty.Premium,
					op: Api.FilterOperator.Gt,
					value: minimumPremium,
				});
			}
			if (maximumPremium) {
				criterias.push({
					property: Api.InsurancePolicyFilterProperty.Premium,
					op: Api.FilterOperator.Lt,
					value: maximumPremium,
				});
			}
		}

		if (!!selectedMinimumRenewalDate || !!selectedMaximumRenewalDate) {
			if (selectedMinimumRenewalDate) {
				criterias.push({
					property: selectedPolicyType,
					op: Api.FilterOperator.Gt,
					value: moment(selectedMinimumRenewalDate).format('YYYY-MM-DD'),
				});
			}

			if (selectedMaximumRenewalDate) {
				criterias.push({
					property: selectedPolicyType,
					op: Api.FilterOperator.Lt,
					value: moment(selectedMaximumRenewalDate).format('YYYY-MM-DD'),
				});
			}
		}

		if (selectedPolicyLineOfBusinessIds.length > 0) {
			criterias.push({
				criteria: selectedPolicyLineOfBusinessIds.map(policyLineOfBusinessId => {
					return {
						property: Api.InsurancePolicyFilterProperty.LineOfBusiness,
						value: policyLineOfBusinessId,
					};
				}),
				op: Api.FilterOperator.Or,
			});
		}

		if (selectedCarrierId) {
			criterias.push({
				property: Api.InsurancePolicyFilterProperty.Carrier,
				value: selectedCarrierId,
			});
		}

		if (!!minimumCommissions || !!maximumCommissions) {
			if (minimumCommissions) {
				criterias.push({
					property: Api.InsurancePolicyFilterProperty.Commissions,
					op: Api.FilterOperator.Gt,
					value: minimumCommissions,
				});
			}

			if (maximumCommissions) {
				criterias.push({
					property: Api.InsurancePolicyFilterProperty.Commissions,
					op: Api.FilterOperator.Lt,
					value: maximumCommissions,
				});
			}
		}

		if (selectedServicingRole1User) {
			criterias.push({
				property: Api.InsurancePolicyFilterProperty.ServicingRole1UserId,
				value: selectedServicingRole1User.id,
			});
		}

		if (selectedServicingRole2User) {
			criterias.push({
				property: Api.InsurancePolicyFilterProperty.ServicingRole2UserId,
				value: selectedServicingRole2User.id,
			});
		}

		return criterias;
	}, [
		debouncedSearchFieldValue,
		selectedContact,
		selectedOwner,
		minimumPremium,
		maximumPremium,
		selectedMinimumRenewalDate,
		selectedMaximumRenewalDate,
		selectedPolicyLineOfBusinessIds,
		selectedCarrierId,
		selectedPolicyType,
		minimumCommissions,
		maximumCommissions,
		selectedServicingRole1User,
		selectedServicingRole2User,
	]);

	const policyFilterQuery = useInsurancePolicyFilter({
		enabled: true,
		filterRequest: {
			criteria: policyFilterCriteria,
		},
	});

	const policySummaryQuery = useInsurancePolicySummaryQuery({
		enabled: true,
		filterRequest: {
			criteria: policyFilterCriteria,
		},
	});

	const refreshPolicies = () => {
		setPolicyToEdit(null);
		invalidateInsurancePolicyFilter({
			filterRequest: {
				criteria: policyFilterCriteria,
			},
		});
		invalidateInsurancePolicySummary({
			filterRequest: {
				criteria: policyFilterCriteria,
			},
		});
	};

	const policyContactsMutation = useInsurancePolicyContactsMutation();
	const exportPoliciesMutation = useExportInsurancePoliciesMutation();
	const [actionButtonsDisabled, setActionButtonsDisabled] = React.useState(false);

	const getMatchingContacts = async () => {
		setActionButtonsDisabled(true);

		let results: Api.IProjectedContact[] = [];

		if (isAllSelected) {
			try {
				const apiResults = await policyContactsMutation.mutateAsync({
					filterRequest: {
						criteria: policyFilterCriteria,
					},
				});

				const excludedContactIdsMap = new Set(excludedContacts.map(contact => contact.id));
				results = apiResults.filter(contact => !excludedContactIdsMap.has(contact.id));
			} catch (error) {
				logApiError('ResolveContacts-Error', error);
			}
		} else {
			results = policies.filter(policy => selectedRows.indexOf(policy.id) > -1).map(policy => policy.contact);
		}
		setActionButtonsDisabled(false);
		return results;
	};

	const policies = React.useMemo(() => {
		return policyFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [policyFilterQuery.data]);

	const {
		isSelectionStateAll,
		selectedRows,
		excludedRows,
		isPartialSelected,
		isAllSelected,
		selectionState,
		isSelected,
		toggleRow,
		toggleSelectAll,
	} = useSelectionState({ rows: policies?.map(p => p.id) });

	const excludedContacts = React.useMemo(() => {
		return excludedRows
			.map(policyId => {
				const policy = policies.find(p => p.id === policyId);

				return policy ? policy.contact : null;
			})
			.filter(Boolean) as Api.IProjectedContact[];
	}, [excludedRows, policies]);

	const clearSearch = () => {
		const donorNameOption = PolicyPropertyOptions[0];
		setSelectedSearchProperty(donorNameOption);
		setSearchFieldValue('');
		setSelectedCarrierId(null);
		setMinimumPremium('');
		setMaximumPremium('');
		setSelectedPolicyLineOfBusinessIds([]);
		setSelectedMinimumRenewalDate(null);
		setSelectedMaximumRenewalDate(null);
		setSelectedContact(null);
		setSelectedOwner(null);
		setMinimumCommissions('');
		setMaximumCommissions('');
		setSelectedServicingRole1User(null);
		setSelectedServicingRole2User(null);
	};

	const onSearchFieldInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchFieldValue(e.target.value);
	};

	const addPolicyClicked = () => {
		setPolicyToEdit({} as Api.IInsurancePolicy);
		setIsAddPopoverOpen(false);
		setIsEditOpen(true);
	};

	const editPolicy = (policy: Api.IInsurancePolicy) => {
		setPolicyToEdit(policy);
		setIsEditOpen(true);
	};

	const deletePolicy = (policy: Api.IInsurancePolicy) => {
		setPolicyToDelete(policy);
		setIsDeleteOpen(true);
	};

	const importClicked = () => {
		history.push(ROUTES.dataBoards.contactSelfImport);
	};

	const selectSearchProperty = (selectedOption: ISelectOption<PolicyPropertyOption>) => {
		setSearchFieldValue('');
		setSelectedCarrierId(null);
		setSelectedOwner(null);
		setSelectedSearchProperty(selectedOption);
		setSelectedServicingRole1User(null);
		setSelectedServicingRole2User(null);
	};

	const onCarrierSearchClear = () => {
		setSelectedCarrierId(null);
	};

	const viewDetails = (contact: Api.IContact) => {
		setContactToView(contact);
		setIsDetailsOpen(true);
	};

	const onDeleteConfirmed = () => {
		setIsDeleteOpen(false);
		refreshPolicies();
	};

	const onExport = () => {
		exportPoliciesMutation.mutate({
			filterRequest: {
				criteria: policyFilterCriteria,
			},
		});
	};

	const onContactCleared = () => {
		setSelectedContact(null);
	};

	const onContactSelected = ({ selection }: { selection: Api.IEntity }) => {
		setSelectedContact(selection);
	};

	const onOwnerCleared = () => {
		setSelectedOwner(null);
	};

	const onOwnerSelected = ({ selection }: { selection: Api.IEntity }) => {
		setSelectedOwner(selection);
	};

	const onServicingRole1UserCleared = () => {
		setSelectedServicingRole1User(null);
	};

	const onServicingRole1UserSelected = ({ selection }: { selection: Api.IEntity }) => {
		setSelectedServicingRole1User(selection);
	};

	const onServicingRole2UserCleared = () => {
		setSelectedServicingRole2User(null);
	};

	const onServicingRole2UserSelected = ({ selection }: { selection: Api.IEntity }) => {
		setSelectedServicingRole2User(selection);
	};

	const { isPending } = policyFilterQuery;

	const noResultsFound = !isPending && policyFilterQuery.isFetched && policies.length === 0;

	const selectCarrier = (carrier: Api.IInsurancePolicyCarrier) => {
		setSelectedCarrierId(carrier.id);
	};

	const VIEW_ONLY_KEY = 'ViewOnlyMode_Policies_Alert';

	const showCard = !!localStorage.getItem(VIEW_ONLY_KEY) !== true;
	const onClose = () => {
		localStorage.setItem(VIEW_ONLY_KEY, 'true');
		refreshFromLocalStorage();
	};

	const refreshFromLocalStorage = () => {
		setKey(uuid());
	};

	const onSendEmailClicked = async () => {
		const matchingContacts = await getMatchingContacts();

		fullscreenModal.history?.push({
			pathname: '/email/campaigns/create/from-selection',
			state: {
				model: {
					context: {
						allowSenderSelection: true,
						bulkFilterRequestOptions: {
							readOnlySearchCriteria: true,
						},
						contacts: matchingContacts.map(c => new Api.ContactViewModel(userSession, c)),
					},
					type: 'Contacts',
				},
			},
		});
	};

	return (
		<Page>
			<MultiContainerHeader
				appBarHeader={
					<BreadcrumbBar
						items={[
							{ label: 'Data Boards', path: ROUTES.dataBoards.home },
							{ label: 'Policies', path: ROUTES.dataBoards.policies },
						]}
						banner={
							!canEdit &&
							showCard && (
								<div className={css(styleSheet.viewOnlyModeBannerContainer)}>
									<div className={css(styleSheet.viewOnlyModeBannerContent)}>
										<LightningBulbIcon />
										<div>
											<h4 className={css(styleSheet.infoHeaderContentBold)}>View Only Mode</h4>
											<div>
												When an integration is enabled you cannot make edits to the policies data through Levitate.
											</div>
										</div>
									</div>
									<CloseButton onClick={onClose} />
								</div>
							)
						}
					/>
				}
			/>

			<Column styles={[styleSheet.container]}>
				<Databoard styles={[styleSheet.container, styleSheet.dataBoardContainer]} key={key}>
					<DataboardFilters>
						<div className={css(dbStyleSheet.propertySearch)}>
							<Select
								value={selectedSearchProperty.id}
								onValueChange={id => selectSearchProperty(PolicyPropertyOptions.find(option => option.id === id))}
							>
								<SelectTrigger styles={[dbStyleSheet.searchPropertySelector]}>
									<SelectValue placeholder='Select sync user' />
								</SelectTrigger>
								<SelectContent>
									{PolicyPropertyOptions.map(option => (
										<SelectItem key={option.id} value={option.id}>
											{option.text}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							{selectedSearchProperty.text === PolicyPropertyOption.PolicyHolder ? (
								<SimpleAutoCompleteSearchField
									onClear={onContactCleared}
									// @ts-ignore
									onItemSelected={onContactSelected}
									resultsLimit={5}
									pageSize={5}
									placeholder='Search'
									type={Api.ResourceAutoCompleteViewModelType.Contact}
									initialSearchQuery=''
									anchorStyles={[dbStyleSheet.comboBoxRightFullHeight, dbStyleSheet.searchFieldReset]}
								/>
							) : null}

							{selectedSearchProperty.text === PolicyPropertyOption.PolicyNumber ? (
								<TextInput
									inputId='policy-number-search-input'
									type='text'
									className={css(dbStyleSheet.comboBoxRightAutocomplete)}
									inputClassName={css(dbStyleSheet.wideInput)}
									onChange={onSearchFieldInputChanged}
									value={searchFieldValue}
									leftAccessory={<SearchIcon className={css(dbStyleSheet.searchIcon)} />}
									placeholder='Search'
								/>
							) : null}

							{selectedSearchProperty.text === PolicyPropertyOption.Carrier ? (
								<PolicyCarrierSearch
									onSelectCarrier={selectCarrier}
									initialSearchFragment=''
									onCreateCarrier={selectCarrier}
									onClear={onCarrierSearchClear}
									className={css(dbStyleSheet.comboBoxRight, dbStyleSheet.wideInput, dbStyleSheet.searchFieldReset)}
								/>
							) : null}

							{selectedSearchProperty.text === PolicyPropertyOption.PolicyOwner ? (
								<SimpleAutoCompleteSearchField
									onClear={onOwnerCleared}
									// @ts-ignore
									onItemSelected={onOwnerSelected}
									resultsLimit={5}
									pageSize={5}
									placeholder='Search'
									type={Api.ResourceAutoCompleteViewModelType.User}
									initialSearchQuery=''
									anchorStyles={[dbStyleSheet.comboBoxRightFullHeight, dbStyleSheet.searchFieldReset]}
								/>
							) : null}

							{canEdit && selectedSearchProperty.text === PolicyPropertyOption.ServicingRole1User ? (
								<SimpleAutoCompleteSearchField
									onClear={onServicingRole1UserCleared}
									// @ts-ignore
									onItemSelected={onServicingRole1UserSelected}
									resultsLimit={5}
									pageSize={5}
									placeholder='Search'
									type={Api.ResourceAutoCompleteViewModelType.User}
									initialSearchQuery=''
									anchorStyles={[dbStyleSheet.comboBoxRightFullHeight, dbStyleSheet.searchFieldReset]}
								/>
							) : null}

							{canEdit && selectedSearchProperty.text === PolicyPropertyOption.ServicingRole2User ? (
								<SimpleAutoCompleteSearchField
									onClear={onServicingRole2UserCleared}
									// @ts-ignore
									onItemSelected={onServicingRole2UserSelected}
									resultsLimit={5}
									pageSize={5}
									placeholder='Search'
									type={Api.ResourceAutoCompleteViewModelType.User}
									initialSearchQuery=''
									anchorStyles={[dbStyleSheet.comboBoxRightFullHeight, dbStyleSheet.searchFieldReset]}
								/>
							) : null}
						</div>

						<div className={css(dbStyleSheet.mediumInput)}>
							<PolicyLineOfBusinessFilterDropdown
								selectedPolicyLineOfBusinessIds={selectedPolicyLineOfBusinessIds}
								setSelectedPolicyLineOfBusinessIds={setSelectedPolicyLineOfBusinessIds}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>

						<div className={css(dbStyleSheet.mediumInput)}>
							<RenewalDateFilterDropdown
								selectedMinimumDate={selectedMinimumRenewalDate}
								setSelectedMinimumDate={setSelectedMinimumRenewalDate}
								selectedMaximumDate={selectedMaximumRenewalDate}
								setSelectedMaximumDate={setSelectedMaximumRenewalDate}
								selectedPolicyType={selectedPolicyType}
								setSelectedPolicyType={setSelectedPolicyType}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>

						<div className={css(dbStyleSheet.mediumInput)}>
							<MoneyFilterDropdown
								key={`premiumFilterDropdown-${minimumPremium}-${maximumPremium}`}
								initialMinimum={minimumPremium}
								initialMaximum={maximumPremium}
								onChange={value => {
									setMinimumPremium(value.minimum);
									setMaximumPremium(value.maximum);
								}}
								fieldName='Premium'
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>

						{canEdit ? (
							<div className={css(dbStyleSheet.mediumInput)}>
								<MoneyFilterDropdown
									key={`commissionsFilterDropdown-${minimumCommissions}-${maximumCommissions}`}
									initialMinimum={minimumCommissions}
									initialMaximum={maximumCommissions}
									onChange={value => {
										setMinimumCommissions(value.minimum);
										setMaximumCommissions(value.maximum);
									}}
									fieldName='Commissions'
									anchorClassName={css(dbStyleSheet.dropdownReset)}
								/>
							</div>
						) : null}

						{hasSearchValues ? (
							<div className={css(dbStyleSheet.clearSearch)}>
								<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={clearSearch}>
									<span>Clear Search</span>
								</button>
							</div>
						) : null}
					</DataboardFilters>

					<DataboardActions>
						<div className={css(dbStyleSheet.leftHeader)}>
							<UsersActionBar
								getMatchingContacts={getMatchingContacts}
								onExport={onExport}
								selectionState={selectionState}
								actionButtonsDisabled={actionButtonsDisabled}
								onSendEmailClicked={onSendEmailClicked}
							/>
						</div>

						{canEdit ? (
							<div className={css(dbStyleSheet.rightHeader)}>
								<TinyPopover
									anchor={
										<div className={css(dbStyleSheet.addButtonContainer)}>
											<button
												className={css(baseStyleSheet.ctaButton, dbStyleSheet.addButton)}
												onClick={addPolicyClicked}
											>
												<span>Add Policy</span>
											</button>
											<button className={css(dbStyleSheet.dropdownCaret)} onClick={() => setIsAddPopoverOpen(true)}>
												<DropDownIcon fillColor={white} />
											</button>
										</div>
									}
									isOpen={isAddPopoverOpen}
									dismissOnOutsideAction={true}
									align='center'
									placement={['bottom']}
									onRequestClose={() => setIsAddPopoverOpen(false)}
								>
									<button
										className={css(baseStyleSheet.ctaButtonReverse, dbStyleSheet.importButton)}
										onClick={importClicked}
									>
										Import from CSV
									</button>
								</TinyPopover>
							</div>
						) : null}
					</DataboardActions>

					<DataboardBody hasFooter>
						<DataGrid styles={[dbStyleSheet.tableOverrides]} isLoading={isPending}>
							<DataGridColumns>
								<DataGridColumn sticky styles={[dbStyleSheet.checkboxColumn]}>
									<Checkbox
										checked={isSelectionStateAll}
										id='select-all-policies'
										onChange={toggleSelectAll}
										partial={isPartialSelected}
									/>
								</DataGridColumn>
								<DataGridColumn sticky>Name</DataGridColumn>
								<DataGridColumn sticky>Line of Business</DataGridColumn>
								<DataGridColumn sticky>Renewal Date</DataGridColumn>
								<DataGridColumn sticky>Premium</DataGridColumn>
								<DataGridColumn sticky>Carrier</DataGridColumn>
								<DataGridColumn sticky>Policy #</DataGridColumn>
								<DataGridColumn sticky>&nbsp;</DataGridColumn>
							</DataGridColumns>

							<DataGridRows>
								{!isPending &&
									policies.map(policy => (
										<DataGridRow key={`policy-${policy.id}`} styles={[dbStyleSheet.row]}>
											<DataGridRowItem>
												<Checkbox
													checked={isSelected(policy.id)}
													id={policy.id}
													readOnly
													onClick={event => {
														event.stopPropagation();
														event.preventDefault();
														toggleRow(policy.id, event.nativeEvent.shiftKey);
													}}
												/>
											</DataGridRowItem>
											<DataGridRowItem>
												<button onClick={() => viewDetails(policy.contact)}>
													<h6 className={css(dbStyleSheet.name, baseStyleSheet.truncateText)}>
														{policy.contact.handle}
													</h6>
												</button>
											</DataGridRowItem>
											<DataGridRowItem>
												{policy.lineOfBusiness ? (
													<Badge
														text={policy.lineOfBusiness.name}
														foregroundColor={policy.lineOfBusiness.foregroundColor}
													/>
												) : null}
											</DataGridRowItem>
											<DataGridRowItem>
												{policy.renewalDate ? moment(policy.renewalDate).format('MM/DD/YYYY') : '-'}
											</DataGridRowItem>
											<DataGridRowItem styles={[dbStyleSheet.amountText]}>
												{policy.premium ? `${numberToUSDCurrencyStringValue(policy.premium)}` : '-'}
											</DataGridRowItem>
											<DataGridRowItem>{policy.carrier?.name}</DataGridRowItem>
											<DataGridRowItem>{policy.policyNumber}</DataGridRowItem>
											<DataGridRowItem>
												{canEdit ? (
													<Dropdown>
														<DropdownEllipsisTrigger />
														<DropdownContent>
															<DropdownItem onClick={() => editPolicy(policy)}>{MenuItem.Edit}</DropdownItem>
															<DropdownItem onClick={() => deletePolicy(policy)}>{MenuItem.Delete}</DropdownItem>
														</DropdownContent>
													</Dropdown>
												) : null}
											</DataGridRowItem>
										</DataGridRow>
									))}

								{isPending ? (
									<DataGridRow>
										<DataGridRowItem colSpan={8}>
											<LoadingSpinner type='large' />
										</DataGridRowItem>
									</DataGridRow>
								) : null}
							</DataGridRows>
						</DataGrid>

						{!policyFilterQuery.isPending && policyFilterQuery.hasNextPage && (
							<Waypoint bottomOffset='-200px' onEnter={() => policyFilterQuery.fetchNextPage()} />
						)}

						{noResultsFound && <DataboardNoResults onClearSearch={clearSearch} />}
					</DataboardBody>

					<DataboardStats>
						<DataboardStatTitle>Policy Stats</DataboardStatTitle>
						<DataboardStat>
							<DataboardStatLabel># Policies</DataboardStatLabel>
							<DataboardStatValue>{policySummaryQuery.data?.totalPolicies || 0}</DataboardStatValue>
						</DataboardStat>

						<DataboardStat>
							<DataboardStatLabel>Total</DataboardStatLabel>
							<DataboardStatValue isTotal>
								{numberToUSDCurrencyStringValue(policySummaryQuery.data?.totalAmount || 0)}
							</DataboardStatValue>
						</DataboardStat>
					</DataboardStats>

					{policyToEdit ? (
						<EditPolicyModal
							initialPolicy={policyToEdit}
							isOpen={isEditOpen}
							setIsOpen={setIsEditOpen}
							onSave={refreshPolicies}
							onCancel={refreshPolicies}
						/>
					) : null}

					{contactToView ? (
						<PolicyHolderDetailsFlyout
							isOpen={isDetailsOpen}
							setIsOpen={setIsDetailsOpen}
							partialContact={contactToView}
						/>
					) : null}

					{policyToDelete ? (
						<DeleteModal
							type={DeleteType.Policy}
							item={policyToDelete}
							isOpen={isDeleteOpen}
							setIsOpen={setIsDeleteOpen}
							onConfirm={onDeleteConfirmed}
						/>
					) : null}

					<ExportConfirmationModalV2
						mutationResult={exportPoliciesMutation}
						modalProps={{
							isOpen: !exportPoliciesMutation.isIdle,
							onRequestClose: () => exportPoliciesMutation.reset(),
						}}
						subTitle={
							<p className={css(dbStyleSheet.noMargin)}>
								The fields we&apos;ll export include: Policy Name, Contact Id, Lifetime Policies, Most Recent Policy
								Amount, Most Recent Policy Date, Most Recent Policy Carrier Name
							</p>
						}
					/>

					<FabContext appearance={{ hidden: true }} />
				</Databoard>
			</Column>
		</Page>
	);
};
